/* eslint-disable eslint-comments/no-restricted-disable, @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointComponent, JSResourceReference } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { isWorkItemModernizationGatesEnabled } from '@jira/issue__eligibility/src/isWorkItemModernizationGatesEnabled.tsx';
import { issueAppEntryPoint } from '@jira/issue__issue-view/src/issueAppEntryPoint.entrypoint.tsx';
import { issueAppSelectorEntryPoint } from '@jira/work-item__issue-app-selector/src/issueAppSelectorEntryPoint.entrypoint.tsx';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	IssuePreviewPanelEntrypointEntrypointProps,
	IssuePreviewPanelEntryPointExtraProps,
	IssuePreviewPanelNestedEntryPoints,
} from './IssuePreviewPanelEntrypoint.entrypoint.types.tsx';

type IssuePreviewPanelRootComponent = EntryPointComponent<
	{},
	IssuePreviewPanelNestedEntryPoints,
	IssuePreviewPanelEntrypointEntrypointProps,
	IssuePreviewPanelEntryPointExtraProps
>;

export const issuePreviewPanelEntryPoint: AnyEntryPoint = createEntryPoint<
	JSResourceReference<IssuePreviewPanelRootComponent>,
	EntryPointRouteParams
>({
	root: JSResourceForUserVisible(
		/* webpackChunkName: "async-issue-preview-panel-entrypoint" */ './index.tsx',
		{ entryFsSsrLiftImportToModule: true },
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		if (
			isWorkItemModernizationGatesEnabled() &&
			expVal('wiv-issue-selector-perf-sprt', 'isEnabled', false)
		) {
			const {
				context: { query },
				tenantContext: { cloudId, isAnonymous },
			} = entryPointParams;
			const issueViewVariant =
				query?.issueViewVariant === 'modern' || query?.issueViewVariant === 'classic'
					? query.issueViewVariant
					: undefined;
			const selectedIssue = query?.selectedIssue ? String(query.selectedIssue) : undefined;

			return {
				...(selectedIssue && {
					entryPoints: {
						issueAppSelectorEntryPoint: {
							entryPoint: issueAppSelectorEntryPoint,
							entryPointParams: {
								issueKey: selectedIssue,
								cloudId,
								isAnonymous,
								isInIssuePreviewPanel: true,
								issueViewVariant,
							},
						},
					},
				}),
				extraProps: { issueViewVariant },
			};
		}

		return {
			entryPoints: {
				issueAppEntryPoint: {
					entryPoint: issueAppEntryPoint,
					entryPointParams,
				},
			},
		};
	},
});
